import React, { useEffect, useState, useRef } from "react";
import { Link, useSearchParams } from "react-router-dom";
import {
    CheckCircle2, ArrowLeft, XCircle, Loader2, Sparkles, Copy, Heart,
} from "lucide-react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/* -------------------------------------------------------------- */
/* Hall-of-Thanks opt-in form                                     */
/* -------------------------------------------------------------- */
function ThanksOptIn({ sessionId }) {
    const [name, setName] = useState("");
    const [note, setNote] = useState("");
    const [submitted, setSubmitted] = useState(false);
    const [error, setError] = useState("");
    const [loading, setLoading] = useState(false);

    const submit = async () => {
        if (!name.trim()) {
            setError("Please enter a first name (or a nickname).");
            return;
        }
        setError("");
        setLoading(true);
        try {
            await axios.post(`${API}/thanks`, {
                session_id: sessionId,
                first_name: name.trim(),
                note: note.trim() || undefined,
            });
            setSubmitted(true);
        } catch (e) {
            setError(e?.response?.data?.detail || "Sorry — couldn't add you. Try again in a moment.");
        }
        setLoading(false);
    };

    if (submitted) {
        return (
            <div className="mt-6 p-5 border-2 border-slate-900 rounded-2xl bg-white text-left" data-testid="thanks-submitted">
                <div className="flex items-center gap-2 font-display font-extrabold text-lg text-slate-900">
                    <Heart size={22} color="#FF5722" fill="#FF5722" /> You're in!
                </div>
                <p className="mt-1 text-slate-700 text-base">
                    Your name will show up on the{" "}
                    <Link to="/thanks" className="underline">Hall of Thanks</Link> in
                    a moment.
                </p>
            </div>
        );
    }

    return (
        <div className="mt-6 p-5 border-2 border-slate-900 rounded-2xl bg-white text-left" data-testid="thanks-optin">
            <div className="flex items-center gap-2 mb-2 text-slate-900 font-display font-extrabold text-lg">
                <Heart size={20} color="#FF5722" fill="#FF5722" /> Join the Hall of Thanks?
            </div>
            <p className="text-slate-700 text-base mb-4">
                Totally optional — if you'd like your first name shown alongside
                other supporters, add it below. You can also leave a short public
                note (140 chars).
            </p>
            <input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="First name or nickname"
                className="w-full px-4 py-3 border-2 border-slate-900 rounded-full text-base mb-3"
                maxLength={40}
                data-testid="thanks-name"
            />
            <textarea
                value={note}
                onChange={(e) => setNote(e.target.value)}
                placeholder="Short note (optional) — 140 characters"
                className="w-full px-4 py-3 border-2 border-slate-900 rounded-2xl text-base"
                maxLength={140}
                rows={2}
                data-testid="thanks-note"
            />
            {error && (
                <p className="mt-2 text-red-700 font-medium text-sm" data-testid="thanks-error">
                    {error}
                </p>
            )}
            <div className="mt-3 flex gap-3">
                <button
                    type="button"
                    onClick={submit}
                    disabled={loading}
                    className="btn-pill solid disabled:opacity-60"
                    data-testid="thanks-submit"
                >
                    <Heart size={18} /> {loading ? "Adding…" : "Add me to the wall"}
                </button>
                <button
                    type="button"
                    onClick={() => setSubmitted(true)}
                    className="btn-pill"
                    data-testid="thanks-skip"
                >
                    No thanks
                </button>
            </div>
        </div>
    );
}

/* -------------------------------------------------------------- */
/* Success page                                                   */
/* -------------------------------------------------------------- */
export function PaymentSuccessPage() {
    const [params] = useSearchParams();
    const sessionId = params.get("session_id");
    const [state, setState] = useState({ status: "polling", payment_status: "pending" });
    const [copied, setCopied] = useState(false);
    const attempts = useRef(0);

    useEffect(() => {
        if (!sessionId) {
            setState({ status: "error", payment_status: "unknown" });
            return;
        }
        let timer;
        const poll = async () => {
            try {
                const { data } = await axios.get(`${API}/payments/status/${sessionId}`);
                if (data.payment_status === "paid") {
                    if (data.lookup_key === "pro_lifetime") {
                        localStorage.setItem("clearcursor_pro", "1");
                        localStorage.setItem("clearcursor_pro_session", data.session_id);
                    }
                    setState(data);
                    return;
                }
                attempts.current += 1;
                if (attempts.current >= 5) {
                    setState({ ...data, status: "timeout" });
                    return;
                }
                timer = setTimeout(poll, 2000);
            } catch (e) {
                setState({ status: "error", payment_status: "unknown" });
            }
        };
        poll();
        return () => timer && clearTimeout(timer);
    }, [sessionId]);

    const isPaid = state.payment_status === "paid";
    const isPolling = state.status === "polling";
    const isPro = isPaid && state.lookup_key === "pro_lifetime";
    const unlockCode = sessionId ? sessionId.slice(-12).toUpperCase() : "";

    const copyCode = () => {
        navigator.clipboard.writeText(unlockCode);
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
    };

    return (
        <div className="min-h-screen grain relative bg-[#F8F9FA] flex items-center justify-center p-6">
            <div
                className="card-bento max-w-lg w-full text-center"
                style={{ background: isPro ? "#FFE9D6" : isPaid ? "#DCFCE7" : "#FFF7E0" }}
            >
                <div className="flex justify-center mb-4">
                    {isPolling && <Loader2 size={64} className="animate-spin" color="#0f172a" />}
                    {isPaid && !isPro && <CheckCircle2 size={72} color="#00A15C" strokeWidth={2.5} />}
                    {isPro && <Sparkles size={72} color="#FF5722" strokeWidth={2.5} />}
                    {!isPolling && !isPaid && <XCircle size={72} color="#B91C1C" strokeWidth={2.5} />}
                </div>
                <h1 className="font-display font-extrabold text-4xl text-slate-900" data-testid="payment-result-title">
                    {isPolling && "Confirming your payment…"}
                    {isPaid && !isPro && "Thank you!"}
                    {isPro && "Welcome to Pro!"}
                    {!isPolling && !isPaid && "Hmm, something's off"}
                </h1>
                <p className="mt-4 text-lg text-slate-800">
                    {isPolling && "Hang tight — we're checking with Stripe."}
                    {isPaid && !isPro && "Your kindness keeps ClearCursor free for everyone. 🧡"}
                    {isPro && "Pro features are now unlocked on this device."}
                    {!isPolling && !isPaid && "We couldn't confirm your payment yet. If money did leave your account, it'll show up shortly — or email chrisparker@gmail.com."}
                </p>

                {isPro && (
                    <div className="mt-6 p-5 border-2 border-slate-900 rounded-2xl bg-white text-left">
                        <div className="text-sm text-slate-700 font-medium">
                            Your restore code — save this to unlock Pro on another device:
                        </div>
                        <div className="mt-2 flex items-center justify-between gap-3">
                            <code
                                className="font-display font-extrabold text-2xl tracking-widest text-slate-900"
                                data-testid="unlock-code"
                            >
                                {unlockCode}
                            </code>
                            <button
                                type="button"
                                onClick={copyCode}
                                className="btn-pill text-sm"
                                data-testid="copy-code-btn"
                            >
                                <Copy size={16} /> {copied ? "Copied!" : "Copy"}
                            </button>
                        </div>
                        <p className="text-sm text-slate-600 mt-3">
                            We also emailed a receipt with this code. Keep it somewhere safe.
                        </p>
                    </div>
                )}

                {isPaid && <ThanksOptIn sessionId={sessionId} />}

                <Link to="/" className="btn-pill solid mt-8 inline-flex" data-testid="return-home-btn">
                    <ArrowLeft size={20} /> Back to ClearCursor
                </Link>
            </div>
        </div>
    );
}

export function PaymentCancelPage() {
    return (
        <div className="min-h-screen grain relative bg-[#F8F9FA] flex items-center justify-center p-6">
            <div className="card-bento max-w-lg w-full text-center" style={{ background: "#FFF7E0" }}>
                <div className="flex justify-center mb-4">
                    <XCircle size={72} color="#0f172a" strokeWidth={2.5} />
                </div>
                <h1 className="font-display font-extrabold text-4xl text-slate-900">
                    No worries at all
                </h1>
                <p className="mt-4 text-lg text-slate-800">
                    You cancelled. ClearCursor is still 100% free for you — enjoy it,
                    and pass it on to someone who could use a brighter pointer.
                </p>
                <Link to="/" className="btn-pill solid mt-8 inline-flex" data-testid="return-home-btn">
                    <ArrowLeft size={20} /> Back to ClearCursor
                </Link>
            </div>
        </div>
    );
}
